import { useState, useEffect } from 'react';

/**
 * Custom hook for debouncing values to improve performance
 *
 * Useful for search inputs and other user interactions that trigger
 * expensive operations. The debounced value will only update after
 * the specified delay period has passed without new changes.
 *
 * @template T - The type of value being debounced
 * @param value - The value to debounce
 * @param delay - Debounce delay in milliseconds (default: 300ms)
 * @returns The debounced value
 *
 * @example
 * ```tsx
 * const [searchQuery, setSearchQuery] = useState('');
 * const debouncedSearchQuery = useDebounce(searchQuery, 300);
 *
 * useEffect(() => {
 *   // This will only fire 300ms after the user stops typing
 *   if (debouncedSearchQuery) {
 *     performSearch(debouncedSearchQuery);
 *   }
 * }, [debouncedSearchQuery]);
 * ```
 */
export function useDebounce<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
